"use client";

import React from "react";
import { ArrowLeft, ExternalLink, Image } from 'lucide-react';
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { AspectRatio } from "@/components/ui/aspect-ratio";
import { PostsProvider, usePosts } from "@/integrations/wordpress/WordPressPostsProvider";
import { WP_Query } from "@/integrations/wordpress/wp_query";

function GalleryItemConsumer() {
  const { posts, loading, error } = usePosts();

  if (loading) {
    return (
      <div data-section-id="160616" className="container mx-auto max-w-4xl px-4">
        <div className="mb-8 h-10 w-32 animate-pulse rounded-md bg-muted"></div>
        <div className="mb-8 overflow-hidden rounded-xl shadow-lg">
          <AspectRatio ratio={16 / 9}>
            <div className="h-full w-full animate-pulse bg-muted"></div>
          </AspectRatio>
        </div>
        <Card className="border-border/50 shadow-xl">
          <CardContent className="p-8 md:p-12">
            <div className="mb-6 h-12 w-3/4 animate-pulse rounded-md bg-muted"></div>
            <div className="space-y-3">
              <div className="h-4 w-full animate-pulse rounded-md bg-muted"></div>
              <div className="h-4 w-full animate-pulse rounded-md bg-muted"></div>
              <div className="h-4 w-5/6 animate-pulse rounded-md bg-muted"></div>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (error) {
    return (
      <div className="container mx-auto max-w-4xl px-4 py-12 text-center">
        <p className="text-destructive font-sans">Failed to load project details. Please try again later.</p>
      </div>
    );
  }

  if (!posts || posts.length === 0) {
    return (
      <div className="container mx-auto max-w-4xl px-4 py-12 text-center">
        <p className="text-muted-foreground font-sans">Roofing project record not found.</p>
        <div className="mt-6">
          <Button asChild variant="outline" className="border-primary text-primary hover:bg-primary hover:text-primary-foreground">
            <Link to="/OurWorkPage">Return to Our Work</Link>
          </Button>
        </div>
      </div>
    );
  }

  const post = posts[0];
  const imageUrl = post.customFields?.image || post.featuredImage;
  const title = post.customFields?.title || post.title;
  const description = post.customFields?.description || post.content?.replace(/<[^>]*>?/gm, '');
  const projectUrl = post.customFields?.project_url;

  return (
    <div className="container mx-auto max-w-4xl px-4">
      <div className="mb-8">
        <Button asChild variant="ghost" className="text-muted-foreground hover:text-primary hover:bg-accent/50">
          <Link to="/OurWorkPage">
            <ArrowLeft className="mr-2 size-4" />
            Back to Our Projects
          </Link>
        </Button>
      </div>

      {imageUrl ? (
        <div className="mb-8 overflow-hidden rounded-xl shadow-2xl ring-1 ring-primary/20">
          <AspectRatio ratio={16 / 9}>
            <img
              src={imageUrl}
              alt={title || "Five Star Roofline Project"}
              className="h-full w-full object-cover"
            />
          </AspectRatio>
        </div>
      ) : (
        <div className="mb-8 overflow-hidden rounded-xl shadow-2xl ring-1 ring-primary/20">
          <AspectRatio ratio={16 / 9}>
            <div className="flex h-full w-full items-center justify-center bg-muted/30">
              <Image className="size-16 text-primary/30" />
            </div>
          </AspectRatio>
        </div>
      )}

      <Card className="border-border/50 shadow-2xl bg-card">
        <CardContent className="p-8 md:p-12">
          {title && (
            <h1 className="mb-6 font-serif text-3xl font-bold tracking-tight text-foreground md:text-5xl">
              {title}
            </h1>
          )}
          
          {description && (
            <div className="prose prose-lg dark:prose-invert mb-8 max-w-none whitespace-pre-wrap text-muted-foreground font-sans leading-relaxed">
              {description}
            </div>
          )}

          {projectUrl && (
            <div className="border-t border-border pt-8">
              <Button asChild size="lg" className="w-full sm:w-auto bg-primary text-primary-foreground hover:bg-primary/90 shadow-lg shadow-primary/20">
                <Link to={projectUrl} newTab>
                  View Project Details
                  <ExternalLink className="ml-2 size-4" />
                </Link>
              </Button>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

export default function SingleGalleryItemTemplateSection() {
  
  const currentWPQueryParams = typeof window !== 'undefined' && window.wvcClient ? window.wvcClient.getCurrentWPQueryParams() : { post_type: 'gallery_item' };
  const wp_query = new WP_Query(currentWPQueryParams);

  return (
    <section id="single-gallery-item-template-section" className="bg-background py-12 md:py-24 text-foreground">
      <PostsProvider wp_query={wp_query}>
        <GalleryItemConsumer />
      </PostsProvider>
    </section>
  );
}